import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { MapPin, DollarSign, Eye } from "lucide-react";
import JobDetailsModal from "@/components/JobDetailsModal";

interface ViewedJob {
  id: string;
  viewed_at: string;
  view_count: number;
  opportunity: {
    id: string;
    title: string;
    company_name: string;
    location: string;
    employment_type: string;
    description: string;
    salary_min: number;
    salary_max: number;
    disclosure_level_required: number;
    requirements: string | null;
    created_at: string;
  };
}

export default function RecentlyViewedJobs() {
  const [viewedJobs, setViewedJobs] = useState<ViewedJob[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedJob, setSelectedJob] = useState<any>(null);
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    fetchViewedJobs();
  }, []);

  const fetchViewedJobs = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from("job_views")
        .select(`
          *,
          opportunity:opportunities (
            id,
            title,
            company_name,
            location,
            employment_type,
            description,
            salary_min,
            salary_max,
            disclosure_level_required,
            requirements,
            created_at
          )
        `)
        .eq("candidate_id", user.id)
        .order("viewed_at", { ascending: false })
        .limit(10);

      if (error) throw error;

      setViewedJobs((data as any) || []);
    } catch (error) {
      console.error("Fetch error:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatSalary = (min: number, max: number) => {
    if (min && max) {
      return `£${min.toLocaleString()} - £${max.toLocaleString()}`;
    }
    return "Competitive";
  };

  const handleViewDetails = (job: ViewedJob) => {
    setSelectedJob(job.opportunity);
    setModalOpen(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin h-6 w-6 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  if (viewedJobs.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Eye className="h-5 w-5" />
            Recently Viewed
          </CardTitle>
          <CardDescription>Jobs you've looked at recently</CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground text-center py-4">
            No recently viewed jobs yet.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Eye className="h-5 w-5" />
            Recently Viewed
          </CardTitle>
          <CardDescription>Jobs you've looked at recently</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {viewedJobs.map((job) => (
            <div
              key={job.id}
              className="flex items-start justify-between gap-4 p-3 border rounded-lg hover:bg-muted/50 cursor-pointer transition-colors"
              onClick={() => handleViewDetails(job)}
            >
              <div className="flex-1 min-w-0">
                <h4 className="font-medium text-sm truncate">{job.opportunity.title}</h4>
                <p className="text-sm text-muted-foreground truncate">{job.opportunity.company_name}</p>
                <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <MapPin className="h-3 w-3" />
                    {job.opportunity.location}
                  </span>
                  <span className="flex items-center gap-1">
                    <DollarSign className="h-3 w-3" />
                    {formatSalary(job.opportunity.salary_min, job.opportunity.salary_max)}
                  </span>
                </div>
              </div>
              <div className="flex flex-col items-end gap-1">
                <Badge variant="outline" className="capitalize text-xs">
                  {job.opportunity.employment_type.replace("-", " ")}
                </Badge>
                {job.view_count > 1 && (
                  <span className="text-xs text-muted-foreground">
                    Viewed {job.view_count}x
                  </span>
                )}
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      <JobDetailsModal
        opportunity={selectedJob}
        open={modalOpen}
        onOpenChange={setModalOpen}
        onApply={() => setModalOpen(false)}
      />
    </>
  );
}
